import React, {FC, ReactNode, RefObject} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontClickEvent} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {PaletteColorsEnum} from '../../../../../../libs/shared/core/src/models/api/paletteModel';
import {ButtonChevronContent} from '../../../../../../libs/shared/ui/src/components/buttons/buttonChevronContent';
import {
  ButtonChrome,
  ButtonInheritableProps,
} from '../../../../../../libs/shared/ui/src/components/buttons/buttonChrome';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonContent} from '../../../../../../libs/shared/ui/src/components/buttons/buttonContent';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisuallyHidden} from '../../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';

/*
 * Props.
 */

interface SendToolbarButtonBaseProps {
  chromeRef: RefObject<HTMLDivElement>;
  variant?: ButtonVariantsEnum;
  color?: PaletteColorsEnum;
  isDisabled?: boolean;
  renderTooltip: () => ReactNode;

  isDefaultDropdownOpen?: boolean;
  onDefaultButtonClick: (event: FrontClickEvent) => void;
  renderDefaultButtonContent: () => ReactNode;
  renderAbbreviatedDefaultButtonContent?: (buttonProps: ButtonInheritableProps) => ReactNode;

  isOptionsDropdownOpen: boolean;
  onOptionsButtonClick: (event: FrontClickEvent) => void;
}

/*
 * Style.
 */

const StyledButtonDiv = styled.div`
  margin: 0 3px;
`;

const StyledChevronButton = styled(ChromelessButton)`
  display: flex;
  align-items: stretch;
`;

/*
 * Component.
 */

export const SendToolbarButtonBase: FC<SendToolbarButtonBaseProps> = (props) => (
  <TooltipCoordinator render={props.renderTooltip}>
    <StyledButtonDiv>
      <ButtonChrome
        ref={props.chromeRef}
        variant={props.variant ?? ButtonVariantsEnum.PRIMARY}
        isDisabled={props.isDisabled}
        color={props.color}
      >
        {(buttonProps) => (
          <>
            <TooltipCoordinator render={props.renderDefaultButtonContent} placement="top">
              <ChromelessButton
                {...buttonProps}
                onClick={props.onDefaultButtonClick}
                hasHover={props.isDefaultDropdownOpen}
                isOpen={props.isDefaultDropdownOpen}
                interactionId="send-toolbar-button"
              >
                <ButtonContent {...buttonProps}>
                  <TooltipOverflowCondition overrideCondition={!!props.renderAbbreviatedDefaultButtonContent}>
                    {props.renderAbbreviatedDefaultButtonContent
                      ? props.renderAbbreviatedDefaultButtonContent(buttonProps)
                      : props.renderDefaultButtonContent()}
                  </TooltipOverflowCondition>
                </ButtonContent>
              </ChromelessButton>
            </TooltipCoordinator>

            <StyledChevronButton
              {...buttonProps}
              onClick={props.onOptionsButtonClick}
              hasHover={props.isOptionsDropdownOpen}
              isOpen={props.isOptionsDropdownOpen}
            >
              <ButtonChevronContent {...buttonProps}>
                <Icon name="chevronDownSmall" />
                <VisuallyHidden>
                  <Trans id="composer-send-toggleoptions">Toggle send options</Trans>
                </VisuallyHidden>
              </ButtonChevronContent>
            </StyledChevronButton>
          </>
        )}
      </ButtonChrome>
    </StyledButtonDiv>
  </TooltipCoordinator>
);
